<script setup>
import { ref, computed, onMounted } from "vue";

import TipTapEditor2 from "@/features/document-editor/ui/TipTapEditor2.vue";
import NotarialActionDataView from "@/pages/admin/home/notarial_actions/tabs/view/NotarialActionDataView.vue";
import Checkbox from "@/shared/ui/checkbox/Checkbox.vue";
import { getSignatureList } from "@/entities/transaction";

const props = defineProps({
  form: {
    type: Object,
    default: () => ({}),
  },
  participants: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  consentChecked: {
    type: Boolean,
    default: false,
  },
  uuid: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["update:consent"]);

const templateLang = ref("ru");
const signatureList = ref([]);

const contentKey = computed(() => {
  return {
    kz: "content_kz",
    ru: "content",
    en: "content_en",
  }[templateLang.value];
});

const templateJson = computed(() => {
  const json = props.form?.[contentKey.value];
  if (!json) return null;
  if (typeof json === "string") {
    try {
      return JSON.parse(json);
    } catch (error) {
      console.error("Invalid template JSON", error);
      return null;
    }
  }
  return json;
});

function changeLang(lang) {
  templateLang.value = lang;
}

async function fetchSignatures() {
  try {
    const { success, payload } = await getSignatureList(props.uuid);
    if (success && payload) {
      signatureList.value = payload.signatures || [];
    }
  } catch (e) {
    console.error("fetchSignatures error:", e);
  }
}

onMounted(() => {
  if (props.uuid) {
    fetchSignatures();
  }
});
</script>

<template>
  <div class="template">
    <div class="block">
      <TipTapEditor2
        :mode="'view'"
        :templateLang="templateLang"
        :json="templateJson"
        :signatures="signatureList"
        @change:lang="changeLang"
      />
      <div class="consent">
        <div class="consent-title">{{ $t("Подтвердите согласие") }}</div>
        <Checkbox
          :model-value="consentChecked"
          class="consent-box"
          @update:modelValue="emit('update:consent', $event)"
        >
          <span class="consent-text pl-2">
            {{ $t("Я ознакомлен (-а) с документом") }}
          </span>
        </Checkbox>
      </div>
    </div>
    <div class="block h-full web-only">
      <NotarialActionDataView
        :loading="loading"
        :form="form"
        :participants="participants"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.template {
  @apply flex h-full w-full overflow-x-hidden gap-5;

  .block {
    @apply flex flex-col w-1/2 h-full  rounded-2xl gap-6 overflow-hidden;
    background-color: hsl(var(--background));
    min-height: 600px !important;
    box-shadow: 0 1px 1px -0.5px rgba(0, 0, 0, 0.03),
      0 3px 3px -1.5px rgba(0, 0, 0, 0.03),
      0 20px 20px -12px rgba(0, 0, 0, 0.03);
  }
}

.consent {
  @apply w-full flex flex-col gap-4 px-6 pb-6;
}

.consent-title {
  @apply text-sm text-muted-foreground;
}

.consent-box {
  @apply w-full py-6 px-4;
  @apply bg-card outline outline-1 outline-offset-[-1px] outline-border;
  border-radius: var(--radius);
}

.consent-text {
  @apply text-sm font-bold text-foreground;
}

@media (max-width: 1400px) {
  .template {
    @apply flex flex-col;

    .block {
      @apply w-full;
    }
  }
}

@media (max-width: 900px) {
  .template {
    .web-only {
      display: none;
    }
  }

  .consent {
    @apply pb-16;
  }
}
</style>
